import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Badge, Card, Empty, ErrorBox, Field, KV, Modal, Page, Select, Spinner, StatusBadge,
  StatusRail, SubmitButton, Timeline, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { COURIER_MODES, FLAG, fmtDate, fmtStamp, inr, lakh, statusHint } from '../../lib/format.js'

export default function OrderDetail () {
  const { id } = useParams()
  const toast = useToast()
  const confirm = useConfirm()
  const { data, error, loading, reload } = useAsync(() => api.get(`/operator/orders/${id}`), [id], { live: ['orders', 'trips', 'batches'] })
  const fleet = useAsync(() => api.get('/operator/fleet'))
  const [modal, setModal] = useState(null)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({})

  /**
   * Every desk action moves money, blocks capacity or makes a promise to a
   * farmer, so each one states its consequence and the values being committed
   * before it fires. The form is filled in first; this is the last gate.
   */
  const confirmFor = (path, body = {}) => {
    const o = data.order
    const commission = (o.totalAmount * o.commissionPct) / 100
    const balance = o.totalAmount - o.advanceAmount
    const sampleSize = o.sample?.animalsSent || data.settings.sample_animals
    const survivors = Number(body.survivors)
    const pct = Math.round((survivors / sampleSize) * 1000) / 10
    const passes = pct >= data.settings.pass_threshold_pct
    const vehicle = (fleet.data?.vehicles || []).find(v => v._id === body.vehicleId)
    const liaison = (fleet.data?.liaisons || []).find(l => l._id === body.liaisonId)

    return {
      advance: {
        title: 'Verify this advance?',
        body: `Only confirm once you can see the money. This blocks ${lakh(o.quantityLakh)} at ${o.hatchery.name} and the sample balloon goes out next.`,
        facts: [['Amount', inr(o.advanceAmount)], ['Reference', body.ref || 'none recorded'], ['Farmer', o.farmer.name]],
        confirmLabel: 'Verify advance'
      },
      'sample-dispatch': {
        title: 'Log the sample as dispatched?',
        body: `${sampleSize} animals must reach ${o.farmer.name} by ${fmtDate(o.sampleDueDate)} for the 24-hour test.`,
        facts: [['Sent by', body.courierMode || 'not stated'], ['Reference', body.courierRef || 'none'], ['Due at the pond', fmtDate(o.sampleDueDate)]],
        confirmLabel: 'Mark dispatched'
      },
      'sample-result': {
        title: passes ? 'Record this as a pass?' : 'Record this as a FAILURE?',
        body: passes
          ? 'The order moves to balance collection.'
          : 'Capacity is released back to the batch, the batch is flagged on quality, and this farmer needs a re-match for the same stocking date.',
        facts: [['Survivors', `${body.survivors} of ${sampleSize}`], ['Survival', `${pct}%`], ['Threshold', `${data.settings.pass_threshold_pct}%`]],
        confirmLabel: passes ? 'Record pass' : 'Record failure',
        tone: passes ? '' : 'danger',
        warning: passes ? null : 'Read the count back to the farmer before you commit this — it cannot be undone from this screen.'
      },
      balance: {
        title: 'Verify the full payment?',
        body: 'Confirm only with the money in hand. The hatchery payout is released next, and hatcheries do not pack before they are paid.',
        facts: [['Balance', inr(balance)], ['Order value', inr(o.totalAmount)], ['Reference', body.ref || 'none recorded']],
        confirmLabel: 'Verify payment'
      },
      payout: {
        title: `Release ${inr(o.totalAmount - commission)} to ${o.hatchery.name}?`,
        body: 'This records the hatchery as paid in full so they can start packing. Money leaving the platform account is not reversible here.',
        facts: [
          ['Payout', inr(o.totalAmount - commission)],
          ['Commission kept', `${inr(commission)} (${o.commissionPct}%)`],
          ['Reference', body.ref || 'none recorded']
        ],
        confirmLabel: 'Release payout',
        tone: 'danger'
      },
      assign: {
        title: 'Assign this vehicle and officer?',
        body: `${o.distanceKm} km from ${o.hatchery.name} to ${o.farmer.village || 'the pond'}. The officer travels with the load and hands over against an OTP.`,
        facts: [
          ['Vehicle', vehicle ? `${vehicle.regNo} — ${vehicle.driverName}` : 'not chosen'],
          ['Officer', liaison ? liaison.name : 'not chosen'],
          ['Stocking date', fmtDate(o.targetStockingDate)]
        ],
        confirmLabel: 'Assign trip'
      },
      complete: {
        title: 'Close this order?',
        body: 'The file is settled and it drops off the desk. Reopening means raising a new booking.',
        facts: [['Order', o.orderCode], ['Farmer', o.farmer.name], ['Value', inr(o.totalAmount)]],
        confirmLabel: 'Close order'
      },
      cancel: {
        title: 'Cancel this order?',
        body: 'Capacity blocked at the hatchery is released back into the batch. Any money already taken has to be settled with the farmer off the platform.',
        facts: [['Order', o.orderCode], ['Farmer', o.farmer.name], ['Reason', body.reason || 'not stated']],
        confirmLabel: 'Cancel order',
        tone: 'danger'
      }
    }[path]
  }

  const act = async (path, body, successKey = 'message') => {
    const ask = confirmFor(path, body)
    if (ask && !(await confirm(ask))) return
    setBusy(true)
    try {
      const res = await api.post(`/operator/orders/${id}/${path}`, body)
      toast[res.passed === false ? 'warn' : 'ok'](res[successKey] || 'Done.')
      setModal(null)
      setForm({})
      reload()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <Page title="Order"><Spinner /></Page>
  if (error) return <Page title="Order"><ErrorBox error={error} onRetry={reload} /></Page>

  const { order: o, trip, replacement, settings } = data
  const balance = o.totalAmount - o.advanceAmount
  const commission = (o.totalAmount * o.commissionPct) / 100
  const open = !['completed', 'cancelled'].includes(o.status)

  return (
    <Page
      crumb={<Link to="/operator/orders">← Orders</Link>}
      title={<>{o.orderCode} {o.isUrgent && <Badge tone="bad">URGENT</Badge>}</>}
      subtitle={`${o.farmer.name} · ${lakh(o.quantityLakh)} ${o.plStage} from ${o.hatchery.name} · stocking ${fmtDate(o.targetStockingDate)}`}
      actions={open ? <button className="btn danger" onClick={() => setModal('cancel')}>Cancel order</button> : null}
    >
      <Card padded={false}>
        <div className="body tight">
          <StatusRail status={o.status} />
        </div>
      </Card>

      {o.replacesOrder && (
        <div className="alert warn">
          <div>
            <b>This is a replacement booking.</b>
            It was raised after <Link to={`/operator/orders/${o.replacesOrder._id}`}>{o.replacesOrder.orderCode}</Link> failed its sample test.
          </div>
        </div>
      )}
      {replacement && (
        <div className="alert info">
          <div>
            <b>Re-matched.</b>
            This farmer was moved to <Link to={`/operator/orders/${replacement._id}`}>{replacement.orderCode}</Link> for the same stocking date.
          </div>
        </div>
      )}

      <div className="split">
        <div>
          <Card title="Next action">
            <div className="action-panel">
              <h3><StatusBadge status={o.status} /></h3>
              <p className="why">{statusHint(o.status)}</p>
              <ActionButtons order={o} trip={trip} onOpen={setModal} />
            </div>
          </Card>

          <Card title="Booking">
            <KV items={[
              { k: 'Farmer', v: <>{o.farmer.name}<span className="t-sub">{o.farmer.phone}</span></> },
              { k: 'Pond', v: <>{[o.farmer.village, o.farmer.mandal, o.farmer.district].filter(Boolean).join(', ')}<span className="t-sub">{o.farmer.pondSalinityPpt ?? '—'} ppt · {o.farmer.pondAreaAcres ?? '—'} acres</span></> },
              { k: 'Hatchery', v: <Link to={`/admin/hatcheries/${o.hatchery._id}`}>{o.hatchery.name}</Link> },
              { k: 'Batch', v: <><span className="mono">{o.batch?.batchCode}</span><span className="t-sub">{o.batch?.broodstockSource}</span></> },
              { k: 'Quantity', v: <>{lakh(o.quantityLakh)}{o.bonusPlLakh > 0 && <span className="t-sub">+{lakh(o.bonusPlLakh)} bonus</span>}</> },
              { k: 'Stocking date', v: <>{fmtDate(o.targetStockingDate)}<span className="t-sub">batch ready {fmtDate(o.matchedReadyDate)}</span></> },
              { k: 'Haul distance', v: `${o.distanceKm} km` },
              { k: 'Batch quality', v: o.batch ? <Badge tone={FLAG[o.batch.qualityFlag].tone}>{FLAG[o.batch.qualityFlag].label}</Badge> : '—' }
            ]} />
            {o.notes && <><div className="divider" /><div className="small">{o.notes}</div></>}
          </Card>

          <Card title="Audit trail">
            <Timeline events={o.events} fmt={fmtStamp} />
          </Card>
        </div>

        <div>
          <Card title="Money">
            <KV items={[
              { k: 'Order value', v: inr(o.totalAmount), big: true },
              { k: 'Rate', v: `${inr(o.unitPricePer1000)} / 1,000 PL` },
              ...(o.discountAmount > 0 ? [{ k: 'Offer discount', v: `− ${inr(o.discountAmount)}` }] : []),
              { k: 'Advance', v: <>{inr(o.advanceAmount)}<span className="t-sub">{o.advancePaidAt ? `received ${fmtStamp(o.advancePaidAt)}` : 'not received'}</span></> },
              { k: 'Balance', v: <>{inr(balance)}<span className="t-sub">{o.balancePaidAt ? `received ${fmtStamp(o.balancePaidAt)}` : 'outstanding'}</span></> },
              { k: `Commission (${o.commissionPct}%)`, v: inr(o.commissionAmount || commission) },
              { k: 'Hatchery payout', v: <>{inr(o.payoutAmount || (o.totalAmount - commission))}<span className="t-sub">{o.payoutAt ? `paid ${fmtStamp(o.payoutAt)}` : 'not released'}</span></> }
            ]} />
            {(o.advanceRef || o.balanceRef || o.payoutRef) && <>
              <div className="divider" />
              <div className="small muted">
                {o.advanceRef && <div>Advance ref: <span className="mono">{o.advanceRef}</span></div>}
                {o.balanceRef && <div>Balance ref: <span className="mono">{o.balanceRef}</span></div>}
                {o.payoutRef && <div>Payout ref: <span className="mono">{o.payoutRef}</span></div>}
              </div>
            </>}
          </Card>

          <Card title="Sample balloon">
            {o.sample?.dispatchedAt
              ? <KV items={[
                { k: 'Dispatched', v: fmtStamp(o.sample.dispatchedAt) },
                { k: 'Sent by', v: <>{o.sample.courierMode || '—'}<span className="t-sub mono">{o.sample.courierRef || ''}</span></> },
                { k: 'Must reach farmer by', v: fmtDate(o.sampleDueDate) },
                { k: 'Animals sent', v: o.sample.animalsSent },
                {
                  k: 'Survival',
                  v: o.sample.survivalPct != null
                    ? <>{o.sample.survivalPct}% <Badge tone={o.sample.result === 'pass' ? 'good' : 'bad'}>{o.sample.result}</Badge>
                      <span className="t-sub">{o.sample.survivors}/{o.sample.animalsSent} alive · threshold {settings.pass_threshold_pct}%</span></>
                    : <span className="muted">awaiting the farmer's count</span>
                },
                ...(o.sample.farmerRemarks ? [{ k: 'Farmer said', v: <span className="small">{o.sample.farmerRemarks}</span> }] : [])
              ]} />
              : <Empty>Not dispatched yet. It must reach the farmer by {fmtDate(o.sampleDueDate)}.</Empty>}
          </Card>

          <Card title="Transport">
            {trip
              ? <>
                <KV items={[
                  { k: 'Trip', v: <span className="mono">{trip.tripCode}</span> },
                  { k: 'Vehicle', v: <>{trip.vehicle?.regNo}<span className="t-sub">{trip.vehicle?.driverName} · {trip.vehicle?.driverPhone}</span></> },
                  { k: 'Liaison officer', v: <>{trip.liaison?.name}<span className="t-sub">{trip.liaison?.phone}</span></> },
                  { k: 'Status', v: <Badge tone={trip.status === 'delivered' ? 'good' : 'info'}>{trip.status.replace(/_/g, ' ')}</Badge> },
                  { k: 'Bags loaded', v: trip.bagsLoaded ?? '—' },
                  { k: 'At packing', v: trip.packedAt ? `${trip.waterTempC ?? '—'}°C · ${trip.salinityPpt ?? '—'} ppt` : '—' },
                  { k: 'Delivered', v: fmtStamp(trip.deliveredAt) },
                  ...(trip.damagedBags > 0 ? [{ k: 'Damaged bags', v: <Badge tone="bad">{trip.damagedBags}</Badge> }] : [])
                ]} />
                {trip.packingPhotoPath && <>
                  <div className="divider" />
                  <a href={trip.packingPhotoPath} target="_blank" rel="noreferrer">
                    <img src={trip.packingPhotoPath} alt="Loading proof" style={{ width: '100%', borderRadius: 8, border: '1px solid var(--line)' }} />
                  </a>
                </>}
                {trip.status !== 'delivered' && (
                  <>
                    <div className="divider" />
                    <Link className="btn" to={`/liaison/trips/${trip._id}`}>Open the liaison field view</Link>
                  </>
                )}
              </>
              : <Empty>No vehicle assigned yet.</Empty>}
          </Card>
        </div>
      </div>

      {modal === 'advance' && (
        <ActionModal title="Verify the advance payment" onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('advance', { ref: form.ref })}
          intro={<>The farmer has transferred <b>{inr(o.advanceAmount)}</b> by UPI or bank transfer. Verifying it here blocks {lakh(o.quantityLakh)} at {o.hatchery.name}.</>}>
          <Field label="UTR / reference" hint="Whatever appears on the bank or UPI statement.">
            <input value={form.ref || ''} onChange={e => setForm({ ...form, ref: e.target.value })} placeholder="UTR 4402118857" />
          </Field>
        </ActionModal>
      )}

      {modal === 'sample' && (
        <ActionModal title="Log the sample balloon dispatch" onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('sample-dispatch', { courierMode: form.courierMode, courierRef: form.courierRef })}
          intro={<>{settings.sample_animals} animals go by public transport and must reach {o.farmer.name} by <b>{fmtDate(o.sampleDueDate)}</b>. The liaison officer is not involved in this leg.</>}>
          <Field label="Sent by">
            <Select options={COURIER_MODES} value={form.courierMode || ''} onChange={v => setForm({ ...form, courierMode: v })} placeholder="Choose…" />
          </Field>
          <Field label="Parcel / LR reference">
            <input value={form.courierRef || ''} onChange={e => setForm({ ...form, courierRef: e.target.value })} placeholder="KKD/PCL/88213" />
          </Field>
        </ActionModal>
      )}

      {modal === 'result' && (
        <ActionModal title="Record the 24-hour survival count" onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('sample-result', { survivors: form.survivors, farmerRemarks: form.farmerRemarks })}
          intro={<>{o.farmer.name} ({o.farmer.phone}) reports how many of the {o.sample?.animalsSent || settings.sample_animals} animals were alive after 24 hours in his pond. {settings.pass_threshold_pct}% or better and the order proceeds.</>}>
          <Field label={`Survivors out of ${o.sample?.animalsSent || settings.sample_animals}`} required>
            <input type="number" min="0" max={o.sample?.animalsSent || settings.sample_animals}
              value={form.survivors ?? ''} onChange={e => setForm({ ...form, survivors: e.target.value })} required autoFocus />
          </Field>
          {form.survivors !== undefined && form.survivors !== '' && (
            <div className={`alert ${Number(form.survivors) / (o.sample?.animalsSent || 100) * 100 >= settings.pass_threshold_pct ? 'ok' : 'error'}`}>
              <div>
                {Math.round((Number(form.survivors) / (o.sample?.animalsSent || 100)) * 1000) / 10}% survival —{' '}
                {Number(form.survivors) / (o.sample?.animalsSent || 100) * 100 >= settings.pass_threshold_pct
                  ? 'passes. The order moves to balance collection.'
                  : 'fails. Capacity is released, the batch is flagged and this farmer needs a re-match.'}
              </div>
            </div>
          )}
          <Field label="What the farmer said">
            <textarea value={form.farmerRemarks || ''} onChange={e => setForm({ ...form, farmerRemarks: e.target.value })}
              placeholder="Pond condition, water clarity, anything relevant to whether it was the seed or the pond" />
          </Field>
        </ActionModal>
      )}

      {modal === 'balance' && (
        <ActionModal title="Verify the full payment" onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('balance', { ref: form.ref })}
          intro={<>Balance outstanding is <b>{inr(balance)}</b> of {inr(o.totalAmount)}. Nothing is packed until this is in hand.</>}>
          <Field label="UTR / reference">
            <input value={form.ref || ''} onChange={e => setForm({ ...form, ref: e.target.value })} />
          </Field>
        </ActionModal>
      )}

      {modal === 'payout' && (
        <ActionModal title="Authorise the hatchery payout" onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('payout', { ref: form.ref })}
          intro={<>{o.hatchery.name} receives <b>{inr(o.totalAmount - commission)}</b> — the full order value less {o.commissionPct}% commission ({inr(commission)}). They will not start packing until this lands.</>}>
          <Field label="Payment reference">
            <input value={form.ref || ''} onChange={e => setForm({ ...form, ref: e.target.value })} placeholder="NEFT OUT 77120045" />
          </Field>
        </ActionModal>
      )}

      {modal === 'assign' && (
        <ActionModal title="Assign vehicle & liaison officer" onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('assign', { vehicleId: form.vehicleId, liaisonId: form.liaisonId })}
          intro={<>{o.distanceKm} km from {o.hatchery.name} to {o.farmer.village || 'the pond'}. The liaison officer travels with the vehicle and hands over against an OTP at the pond.</>}>
          <Field label="Vehicle" required>
            <Select
              options={(fleet.data?.vehicles || []).map(v => ({ value: v._id, label: `${v.regNo} — ${v.driverName} (${v.capacityBags} bags)` }))}
              value={form.vehicleId || ''} onChange={v => setForm({ ...form, vehicleId: v })} placeholder="Choose a pre-verified vehicle…" required />
          </Field>
          <Field label="Liaison officer" required>
            <Select
              options={(fleet.data?.liaisons || []).map(l => ({ value: l._id, label: `${l.name} — ${l.baseLocation || ''}` }))}
              value={form.liaisonId || ''} onChange={v => setForm({ ...form, liaisonId: v })} placeholder="Choose an officer…" required />
          </Field>
        </ActionModal>
      )}

      {modal === 'complete' && (
        <ActionModal title="Close this order" onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('complete', { notes: form.notes })}
          intro="Seed is stocked and the file is settled.">
          <Field label="Closing note">
            <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </ActionModal>
      )}

      {modal === 'cancel' && (
        <ActionModal title="Cancel this order" danger onClose={() => setModal(null)} busy={busy}
          onSubmit={() => act('cancel', { reason: form.reason })}
          intro="Any capacity blocked at the hatchery is released back into the batch.">
          <Field label="Reason" required>
            <input value={form.reason || ''} onChange={e => setForm({ ...form, reason: e.target.value })} required autoFocus />
          </Field>
        </ActionModal>
      )}
    </Page>
  )
}

function ActionButtons ({ order: o, trip, onOpen }) {
  switch (o.status) {
    case 'awaiting_advance':
      return <div className="btnrow"><button className="btn primary" onClick={() => onOpen('advance')}>Advance received</button></div>
    case 'advance_verified':
      return <div className="btnrow"><button className="btn primary" onClick={() => onOpen('sample')}>Sample balloon dispatched</button></div>
    case 'sample_dispatched':
      return <div className="btnrow"><button className="btn primary" onClick={() => onOpen('result')}>Record survival result</button></div>
    case 'sample_passed':
      return <div className="btnrow"><button className="btn primary" onClick={() => onOpen('balance')}>Full payment received</button></div>
    case 'sample_failed':
      return (
        <div className="btnrow">
          <Link className="btn primary" to={`/operator/match?rematchFrom=${o._id}`}>Find an alternative batch</Link>
          <Link className="btn" to={`/operator/batches/${o.batch?._id}/flag`}>Batch decision screen</Link>
        </div>
      )
    case 'balance_verified':
      return <div className="btnrow"><button className="btn primary" onClick={() => onOpen('payout')}>Authorise hatchery payout</button></div>
    case 'payout_released':
      return <div className="btnrow"><button className="btn primary" onClick={() => onOpen('assign')}>Assign vehicle & liaison</button></div>
    case 'trip_assigned':
    case 'packed':
    case 'in_transit':
      return (
        <div className="btnrow">
          <Link className="btn primary" to={`/liaison/trips/${trip?._id}`}>Open liaison field view</Link>
          <span className="muted small">The liaison officer logs packing and delivery from the field.</span>
        </div>
      )
    case 'delivered':
      return <div className="btnrow"><button className="btn primary" onClick={() => onOpen('complete')}>Close the order</button></div>
    default:
      return <div className="muted small">No action outstanding.</div>
  }
}

function ActionModal ({ title, intro, children, onSubmit, onClose, busy, danger }) {
  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={e => { e.preventDefault(); onSubmit() }}>
        {intro && <p className="muted small" style={{ marginTop: 0 }}>{intro}</p>}
        {children}
        <div className="btnrow">
          <SubmitButton busy={busy} type="submit" className={`btn ${danger ? 'danger' : 'primary'}`}>Confirm</SubmitButton>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  )
}
